// The one list of public pages (everything outside /dashboard and /auth).
// Plain JS with no imports, so the app and the build scripts read the same file:
//   - hooks/useFetchUserDetails.js: which addresses a signed-in recruiter may
//     stay on (anything else is sent to the dashboard);
//   - app/ProtectedRoute.jsx: which frame a page gets;
//   - scripts/check-route-reachability.mjs: fails the build when a route in
//     src/router.tsx is missing here.
// The free tools keep their own list in lib/free-tools/registry.js, and the
// pages with a prerendered head are listed in scripts/generate-seo-heads.mjs,
// which fails the build when nginx.conf does not serve one of them.
//
// Each entry:
//   path       the address, or the start of it.
//   open       a signed-in recruiter stays on any address that BEGINS with
//              path. This is a plain prefix match, so "/interview" also covers
//              "/interview-questions", and an unknown address that begins
//              with an entry ("/blogx") shows the not-found page and is not
//              sent to the dashboard.
//   marketing  the page gets the marketing frame:
//                "exact"  path only
//                "tree"   path and everything under it
//                "under"  only what is under it
//                "slug"   path, or path plus one lowercase slug
//   bare       no site header or footer when the marketing frame does not apply.

/** @type {{ path: string, open?: boolean, marketing?: "exact" | "tree" | "under" | "slug", bare?: boolean }[]} */
export const PUBLIC_PAGES = [
  // "/" stays open by an exact match in isOpenWhenSignedIn; as a prefix it
  // would match every address.
  { path: "/", marketing: "exact" },
  { path: "/about", open: true, marketing: "exact" },
  { path: "/contact", open: true, marketing: "exact" },
  { path: "/newsletter", open: true, marketing: "exact" },
  { path: "/book-a-demo", open: true, marketing: "exact" },
  { path: "/pricing", open: true, marketing: "exact" },
  { path: "/candidates", open: true, marketing: "exact" },
  { path: "/connect", open: true, marketing: "exact" },
  { path: "/privacy-policy", open: true, marketing: "exact", bare: true },
  { path: "/terms-of-use", open: true, marketing: "exact", bare: true },
  { path: "/refund-policy", open: true, marketing: "exact", bare: true },

  { path: "/blog", open: true, marketing: "tree" },
  { path: "/recruiters", open: true, marketing: "tree" },
  { path: "/features", open: true, marketing: "under" },
  // Open through isFreeToolPath (lib/free-tools/registry.js), one page at a time.
  { path: "/tools", marketing: "tree" },

  // Content hubs (lib/hubs).
  { path: "/interview-questions", open: true, marketing: "slug" },
  { path: "/compare", open: true, marketing: "slug" },
  { path: "/industries", open: true, marketing: "slug" },
  { path: "/integrations", open: true, marketing: "slug" },
  { path: "/security", open: true, marketing: "exact" },
  { path: "/languages", open: true, marketing: "exact" },
  // Native-language recruiter pages (lib/native/aiiaLanding.js). As an open
  // prefix "/de" also covers "/demo", which is public anyway.
  { path: "/de", open: true, marketing: "exact" },
  { path: "/fr", open: true, marketing: "exact" },
  { path: "/es", open: true, marketing: "exact" },
  { path: "/sv", open: true, marketing: "exact" },
  { path: "/it", open: true, marketing: "exact" },
  // Native-language candidate pages: /<code>/features/<slug> (lib/native/candidatePages.js).
  // "under" gives them the marketing frame, which renders them without the English header.
  { path: "/de/features", open: true, marketing: "under" },
  { path: "/fr/features", open: true, marketing: "under" },
  { path: "/es/features", open: true, marketing: "under" },
  { path: "/sv/features", open: true, marketing: "under" },
  { path: "/it/features", open: true, marketing: "under" },
  { path: "/ja/features", open: true, marketing: "under" },
  { path: "/fil/features", open: true, marketing: "under" },
  { path: "/th/features", open: true, marketing: "under" },
  { path: "/id/features", open: true, marketing: "under" },
  // The printable screening kit a lead magnet email links to.
  { path: "/kits", open: true, marketing: "slug" },

  // Pages a candidate or a guest opens from a link.
  { path: "/interview", open: true, bare: true },
  { path: "/i/", open: true },
  { path: "/my-interviews", open: true, bare: true },
  { path: "/careers", open: true, bare: true },
  { path: "/certificate", open: true },
  { path: "/shared-video", open: true },
  { path: "/shared-clip", open: true },
  { path: "/report-preview", open: true },
];

// The app itself. Not public pages, and never listed above.
export const APP_BASES = ["/dashboard", "/auth"];

/** A signed-in recruiter may stay on this address. */
export function isOpenWhenSignedIn(pathname) {
  return (
    pathname === "/" ||
    PUBLIC_PAGES.some((page) => page.open && pathname.startsWith(page.path))
  );
}

const MARKETING_MATCH = {
  exact: (pathname, path) => pathname === path,
  tree: (pathname, path) => pathname === path || pathname.startsWith(`${path}/`),
  under: (pathname, path) => pathname.startsWith(`${path}/`),
  slug: (pathname, path) =>
    pathname === path ||
    (pathname.startsWith(`${path}/`) && /^[a-z0-9-]+$/.test(pathname.slice(path.length + 1))),
};

/** The page gets the marketing frame. */
export function isMarketingPage(pathname) {
  return PUBLIC_PAGES.some(
    (page) => page.marketing && MARKETING_MATCH[page.marketing](pathname, page.path)
  );
}

/** No site header or footer (only asked when isMarketingPage is false). */
export function isBarePage(pathname) {
  return (
    APP_BASES.some((base) => pathname.startsWith(base)) ||
    PUBLIC_PAGES.some((page) => page.bare && pathname.startsWith(page.path))
  );
}
